import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormInput } from '../_form-input';
import { ChangeDetectorRef } from '@angular/core';

import { v4 as uuid } from 'uuid';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';

@Component({
  selector: 'app-shared-form-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextComponent),
      multi: true,
    },
  ],
})
export class InputTextComponent implements FormInput, ControlValueAccessor {
  // value is managed by the ControlValueAccessor lifecycle (writeValue)
  // Remove @Input() to avoid parent bindings clashing with the FormControl value
  private _value: any;
  get value() {
    return this._value;
  }
  set value(v: any) {
    this._value = v;
  }
  @Input() id: string = uuid();
  @Input() name: string = 'id';
  @Input() label: string = 'Input';
  @Input() placeholder?: string;
  @Input() mask?: string;

  @Input() error: string = '';

  @Input() type: string = 'text';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;

  @Input() passwordInput: boolean = false;

  @Input() autocompleteAnchor?: MatAutocomplete;

  /** If true, hide the label element */
  @Input() noLabel: boolean = false;

  /** Name of a material icon to show inside the input on the left */
  @Input() prefixIcon?: string;

  /** Size of the input. 'medium' uses the current 16px padding, 'small' uses reduced padding */
  @Input() size: 'medium' | 'small' = 'medium';

  @Input() showSuccessIcon: boolean = false;

  visibleValue: boolean = false;

  @Output() onChange$: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFocus$: EventEmitter<void> = new EventEmitter<void>();
  @Output() focusOut$: EventEmitter<void> = new EventEmitter<void>();
  @Output() enterPress: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(MatAutocompleteTrigger, { static: false })
  autocompleteTrigger?: MatAutocompleteTrigger;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private cd: ChangeDetectorRef) {}

  writeValue(value: any): void {
    console.log('[INPUT-TEXT] 🔵 writeValue called', {
      value,
      valueType: typeof value,
      isUndefined: value === undefined,
      isNull: value === null,
      isEmpty: value === '',
      id: this.id,
      name: this.name,
      label: this.label
    });
    // Accept null/'' values coming from FormControl. Only ignore undefined
    if (value !== undefined) {
      this._value = value;
      console.log('[INPUT-TEXT] 🟢 value set to:', this._value);
      // ensure view updates even if writeValue is called outside a normal CD cycle
      try {
        this.cd.detectChanges();
      } catch (e) {
        // ignore; detectChanges can throw if called during certain phases
      }
    } else {
      console.log('[INPUT-TEXT] ⚠️ value is undefined, not setting');
    }
  }

  registerOnChange(fn: any): void {
    console.log('[INPUT-TEXT] 🔵 registerOnChange called', {
      id: this.id,
      name: this.name,
      label: this.label
    });
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    console.log('[INPUT-TEXT] 🔵 registerOnTouched called', {
      id: this.id,
      name: this.name,
      label: this.label
    });
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  emitChange(value: any): void {
    this._value = value;
    this.onChange(value);
    this.onChange$.emit(value);
  }

  emitFocus(): void {
    // focus event — do not call onTouched here. onTouched should be called on blur (focus out).
    this.onFocus$.emit();
  }

  emitFocusOut(): void {
    // mark as touched when the input loses focus
    this.onTouched();
    this.focusOut$.emit();
  }

  get matAutocompleteTrigger() {
    return this.autocompleteTrigger;
  }

  autoGrow(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto';
    let lines = textarea.value.split('\n').length;
    if (lines > 2) lines = 2;
    textarea.rows = lines;

    const lineHeight = parseInt(
      window.getComputedStyle(textarea).lineHeight || '20',
      10
    );
    const maxHeight = lineHeight * 2 + 8;
    textarea.style.height =
      Math.min(textarea.scrollHeight + 4, maxHeight) + 'px';
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  onTextareaKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.enterPress.emit();
    }
  }
}
